import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  api,
  type SubscriptionKpiSnapshot,
  type SubscriptionWeekItem,
} from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";
import { OpCallout } from "@/components/op/OpCallout";

function currentWeek() {
  const d = new Date();
  const t = new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()));
  const day = t.getUTCDay() || 7;
  t.setUTCDate(t.getUTCDate() + 4 - day);
  const yearStart = new Date(Date.UTC(t.getUTCFullYear(), 0, 1));
  const week = Math.ceil(((t.getTime() - yearStart.getTime()) / 86400000 + 1) / 7);
  return `${t.getUTCFullYear()}-W${String(week).padStart(2, "0")}`;
}

const SECTION_ORDER = ["retencja", "akwizycja", "ops"] as const;
const SECTION_LABEL: Record<string, string> = {
  retencja: "Retencja (zadowolenie)",
  akwizycja: "Akwizycja (nowi)",
  ops: "Ops",
};
const SECTION_SHORT: Record<string, string> = {
  retencja: "Retencja",
  akwizycja: "Akwizycja",
  ops: "Ops",
};
const SECTION_TONE: Record<string, string> = {
  retencja: "border-emerald-200/80 bg-emerald-50/50",
  akwizycja: "border-sky-200/80 bg-sky-50/50",
  ops: "border-amber-200/80 bg-amber-50/50",
};

function groupWeek(items: SubscriptionWeekItem[]) {
  const map: Record<string, SubscriptionWeekItem[]> = {
    retencja: [],
    akwizycja: [],
    ops: [],
  };
  for (const it of items) {
    const key = SECTION_ORDER.includes(it.section as (typeof SECTION_ORDER)[number])
      ? it.section
      : "ops";
    map[key].push(it);
  }
  return map;
}

function ChecklistRow(props: {
  id: string;
  label: string;
  owner: string;
  description?: string;
  done: boolean;
  disabled?: boolean;
  onToggle: (done: boolean) => void;
}) {
  const { label, owner, description, done, disabled, onToggle } = props;
  return (
    <li className="grid gap-3 rounded-lg border border-border/70 bg-background/40 p-3 sm:grid-cols-[minmax(0,1fr)_minmax(0,1.35fr)]">
      <div className="flex items-start gap-3 text-sm">
        <input
          type="checkbox"
          className="mt-1 shrink-0"
          checked={done}
          disabled={disabled}
          onChange={(e) => onToggle(e.target.checked)}
        />
        <div className="min-w-0">
          <div className={cn("font-medium", done && "line-through text-muted-foreground")}>{label}</div>
          <div className="text-xs text-muted-foreground mt-0.5">Owner: {owner}</div>
        </div>
      </div>
      <div className="text-sm text-muted-foreground leading-relaxed border-t border-border/50 pt-2 sm:border-t-0 sm:border-l sm:pt-0 sm:pl-3">
        <div className="text-[11px] uppercase tracking-wide text-muted-foreground/80 mb-1">Jak zrobić</div>
        <p className="whitespace-pre-wrap">{description || "Brak opisu w seedzie."}</p>
      </div>
    </li>
  );
}

export default function Subskrypcje() {
  const qc = useQueryClient();
  const [week, setWeek] = useState(currentWeek);
  const summaryQ = useQuery({
    queryKey: ["subscription-ops-summary"],
    queryFn: () => api.subscriptionSummary(),
  });
  const weekQ = useQuery({
    queryKey: ["subscription-ops-week", week],
    queryFn: () => api.subscriptionWeek(week),
  });

  const launchM = useMutation({
    mutationFn: ({ id, done }: { id: string; done: boolean }) =>
      api.subscriptionLaunchCheck(id, done),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["subscription-ops-summary"] }),
  });
  const weekM = useMutation({
    mutationFn: ({ id, done }: { id: string; done: boolean }) =>
      api.subscriptionWeekCheck(week, id, done),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["subscription-ops-week", week] });
      qc.invalidateQueries({ queryKey: ["subscription-ops-summary"] });
    },
  });

  const [kpiForm, setKpiForm] = useState({
    active_subscribers: 1,
    plan_basic: 1,
    plan_pro: 0,
    new_this_week: 0,
    churned_this_week: 0,
    rewards_usd: 77.16,
    note: "",
  });
  const kpiM = useMutation({
    mutationFn: () => api.subscriptionKpiSave({ week, ...kpiForm }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["subscription-ops-summary"] }),
  });

  const offer = summaryQ.data?.offer;
  const launch = summaryQ.data?.launch;
  const weekItems = weekQ.data?.items || summaryQ.data?.week?.items || [];
  const grouped = useMemo(() => groupWeek(weekItems), [weekItems]);
  const history: SubscriptionKpiSnapshot[] = summaryQ.data?.kpi_history || [];
  const latest = summaryQ.data?.kpi_latest;
  const weekDone = weekItems.filter((it) => it.done).length;
  const weekTotal = weekItems.length;

  return (
    <div className="space-y-6 pb-10">
      <section className="sticky top-0 z-10 -mx-3 border-b border-border bg-card/95 px-3 py-3 shadow-sm backdrop-blur md:-mx-8 md:px-8">
        <div className="mb-2 flex flex-wrap items-center gap-2">
          <h2 className="text-sm font-semibold tracking-tight">Plan tygodnia</h2>
          <span className="rounded-full bg-muted px-2 py-0.5 text-[11px] text-muted-foreground">
            {week}
          </span>
          <span className="text-[11px] text-muted-foreground">
            {weekDone}/{weekTotal} gotowe
          </span>
          <div className="ml-auto flex items-center gap-2">
            <Input
              className="h-8 w-32 text-xs"
              value={week}
              onChange={(e) => setWeek(e.target.value.trim())}
              placeholder="2026-W36"
              aria-label="ISO tydzień"
            />
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="h-8"
              onClick={() => setWeek(currentWeek())}
            >
              Bieżący
            </Button>
          </div>
        </div>
        <div className="flex gap-2 overflow-x-auto pb-1">
          {SECTION_ORDER.map((sec) => {
            const items = grouped[sec];
            if (!items.length) return null;
            return (
              <div
                key={sec}
                className={cn(
                  "flex min-w-max items-stretch gap-1.5 rounded-lg border px-2 py-1.5",
                  SECTION_TONE[sec],
                )}
              >
                <div className="flex items-center px-1 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
                  {SECTION_SHORT[sec]}
                </div>
                {items.map((it) => (
                  <label
                    key={it.id}
                    className={cn(
                      "flex max-w-[11rem] cursor-pointer items-start gap-1.5 rounded-md border border-border/60 bg-background/90 px-2 py-1.5 text-xs shadow-sm",
                      it.done && "opacity-70",
                    )}
                    title={it.description || it.label}
                  >
                    <input
                      type="checkbox"
                      className="mt-0.5 shrink-0"
                      checked={!!it.done}
                      disabled={weekM.isPending}
                      onChange={(e) => weekM.mutate({ id: it.id, done: e.target.checked })}
                    />
                    <span className="min-w-0">
                      <span
                        className={cn(
                          "block leading-snug",
                          it.done && "line-through text-muted-foreground",
                        )}
                      >
                        {it.label}
                      </span>
                      <span className="mt-0.5 block text-[10px] text-muted-foreground">
                        {it.owner}
                      </span>
                    </span>
                  </label>
                ))}
              </div>
            );
          })}
        </div>
      </section>

      <PageHeader
        title="Subskrypcje TikTok"
        subtitle="Pilot H-010: start, rytm tygodnia, KPI z apki. Skala zostaje przy pakiecie 99 zł."
      />

      {offer && (
        <section className="rounded-xl border border-border bg-card p-5 space-y-3">
          <div className="flex flex-wrap items-center gap-2">
            <h2 className="text-lg font-semibold">{offer.title}</h2>
            <span className="text-xs rounded-full bg-muted px-2 py-0.5">{offer.pilot_id}</span>
          </div>
          <p className="text-sm text-foreground/90 max-w-3xl">{offer.promise}</p>
          <p className="text-xs text-muted-foreground">{offer.panel_note}</p>
          <div className="grid gap-3 sm:grid-cols-2">
            {offer.plans.map((p) => (
              <div key={p.name} className="rounded-lg border border-border/80 p-3">
                <div className="font-medium">
                  {p.name} · {p.price_pln.toFixed(2)} PLN
                </div>
                <ul className="mt-2 text-sm text-muted-foreground list-disc pl-4 space-y-0.5">
                  {p.perks.map((x) => (
                    <li key={x}>{x}</li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
          <div className="flex flex-wrap gap-2 text-sm">
            <Link className="underline" to="/wiedza?view=biblioteka&id=subskrypcja-tiktok">
              Dokumentacja w Wiedzy
            </Link>
            <span className="text-muted-foreground">{offer.docs_path}</span>
          </div>
        </section>
      )}

      <OpCallout variant="help" title="Start">
        Odhacz ustawienia w apce TikTok. Przy każdym punkcie jest kolumna „Jak zrobić”. Progress:{" "}
        {launch?.done ?? 0}/{launch?.total ?? 0} ({launch?.pct ?? 0}%).
      </OpCallout>

      <section className="rounded-xl border border-border bg-card p-5">
        <h2 className="text-base font-semibold mb-3">Checklista uruchomienia</h2>
        <ul className="space-y-3">
          {(launch?.items || []).map((it) => (
            <ChecklistRow
              key={it.id}
              id={it.id}
              label={it.label}
              owner={it.owner}
              description={it.description}
              done={it.done}
              disabled={launchM.isPending}
              onToggle={(done) => launchM.mutate({ id: it.id, done })}
            />
          ))}
        </ul>
      </section>

      <section className="rounded-xl border border-border bg-card p-5 space-y-4">
        <div className="flex flex-wrap items-end gap-3">
          <h2 className="text-base font-semibold">Plan tygodnia</h2>
          <Input
            className="w-36"
            value={week}
            onChange={(e) => setWeek(e.target.value.trim())}
            placeholder="2026-W36"
          />
          <Button type="button" variant="outline" size="sm" onClick={() => setWeek(currentWeek())}>
            Bieżący
          </Button>
        </div>
        {SECTION_ORDER.map((sec) => (
          <div key={sec}>
            <h3 className="text-sm font-medium mb-2">{SECTION_LABEL[sec]}</h3>
            <ul className="space-y-3 mb-4">
              {grouped[sec].map((it) => (
                <ChecklistRow
                  key={it.id}
                  id={it.id}
                  label={it.label}
                  owner={it.owner}
                  description={it.description}
                  done={!!it.done}
                  disabled={weekM.isPending}
                  onToggle={(done) => weekM.mutate({ id: it.id, done })}
                />
              ))}
            </ul>
          </div>
        ))}
      </section>

      <section className="rounded-xl border border-border bg-card p-5 space-y-4">
        <h2 className="text-base font-semibold">Statystyki (ręczne z apki)</h2>
        {latest ? (
          <p className="text-sm text-muted-foreground">
            Ostatni odczyt {latest.week}: aktywni {latest.metrics.active_subscribers}, Basic{" "}
            {latest.metrics.plan_basic}, Pro {latest.metrics.plan_pro}, rewards $
            {latest.metrics.rewards_usd} ({latest.recorded_at.slice(0, 10)})
          </p>
        ) : (
          <p className="text-sm text-amber-700">Brak odczytu KPI. Bart przypomni po 7 dniach.</p>
        )}
        <div className="grid gap-3 sm:grid-cols-3">
          {(
            [
              ["active_subscribers", "Aktywni"],
              ["plan_basic", "Plan Basic"],
              ["plan_pro", "Plan Pro"],
              ["new_this_week", "Nowi w tyg."],
              ["churned_this_week", "Odejścia"],
              ["rewards_usd", "Rewards USD"],
            ] as const
          ).map(([key, label]) => (
            <label key={key} className="text-sm space-y-1">
              <span className="text-muted-foreground">{label}</span>
              <Input
                type="number"
                step={key === "rewards_usd" ? "0.01" : "1"}
                value={kpiForm[key]}
                onChange={(e) =>
                  setKpiForm((f) => ({
                    ...f,
                    [key]:
                      key === "rewards_usd"
                        ? Number(e.target.value)
                        : parseInt(e.target.value || "0", 10),
                  }))
                }
              />
            </label>
          ))}
        </div>
        <Input
          placeholder="Notatka (opcjonalnie)"
          value={kpiForm.note}
          onChange={(e) => setKpiForm((f) => ({ ...f, note: e.target.value }))}
        />
        <Button type="button" onClick={() => kpiM.mutate()} disabled={kpiM.isPending}>
          Zapisz KPI dla {week}
        </Button>
        {history.length > 1 && (
          <div className="text-xs text-muted-foreground space-y-1">
            <div className="font-medium text-foreground">Historia</div>
            {history.map((h) => (
              <div key={h.week}>
                {h.week}: {h.metrics.active_subscribers} aktywnych (Δ+{h.metrics.new_this_week}/-
                {h.metrics.churned_this_week})
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
